'use strict';

/* ==========================================================================
   Noveliya — Supply Chain Animation (infinite loop)
   ========================================================================== */

class NvSupplyChain {
  constructor(el) {
    this.el = el;
    this.prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.observe();
  }

  observe() {
    if (this.prefersReduced) { this.showAll(); return; }

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        io.disconnect();
        this.run();
      }
    }, { threshold: 0.15 });

    io.observe(this.el);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  wait(ms) { return new Promise(r => setTimeout(r, ms)); }

  nodePositions(scene) {
    const W = scene.offsetWidth;
    return Array.from(scene.querySelectorAll('[data-node]'))
      .sort((a, b) => +a.dataset.node - +b.dataset.node)
      .map(node => parseFloat(node.dataset.pos) / 100 * W);
  }

  moveTruck(vehicle, x, ms = 0, easing = 'ease-in-out') {
    const halfW = vehicle.offsetWidth / 2;
    vehicle.style.transition = ms > 0 ? `left ${ms}ms ${easing}` : 'none';
    if (ms === 0) vehicle.getBoundingClientRect();
    vehicle.style.left = `${x - halfW}px`;
  }

  /**
   * Truck start x: offset right of first node so it clears the building icon.
   * Truck end x: offset left of last node so it doesn't cover the building.
   */
  startX(vehicle, pos) {
    return pos[0] + vehicle.offsetWidth / 2 + 22;
  }
  endX(vehicle, pos) {
    return pos[pos.length - 1] - vehicle.offsetWidth / 2 - 14;
  }

  showNode(scene, idx) {
    const pip = scene.querySelector(`[data-dot="${idx}"]`);
    if (pip) pip.classList.add('is-visited');

    [`[data-node-connector="${idx}"]`, `[data-node-meta="${idx}"]`].forEach(sel => {
      const el = scene.querySelector(sel);
      if (el) el.classList.add('is-visible');
    });
  }

  async showClock(clockEl, ms) {
    clockEl.classList.add('is-visible', 'is-ticking');
    await this.wait(ms - 200);
    clockEl.classList.remove('is-visible', 'is-ticking');
    await this.wait(200);
  }

  // ── Reduced motion: reveal everything immediately ──────────────────────────

  showAll() {
    this.el.querySelectorAll('[data-scene]').forEach(scene => {
      scene.querySelectorAll('[data-node-connector], [data-node-meta]')
        .forEach(el => el.classList.add('is-visible'));
      scene.querySelectorAll('[data-dot]')
        .forEach(el => el.classList.add('is-visited'));

      const fill    = scene.querySelector('.nv-sc__rail-fill');
      const vehicle = scene.querySelector('[data-vehicle]');
      if (!fill || !vehicle) return;

      fill.style.width = '100%';
      const pos = this.nodePositions(scene);
      if (pos.length) this.moveTruck(vehicle, this.endX(vehicle, pos), 0);
      vehicle.classList.add('is-visible');
    });

    this.el.querySelectorAll('.nv-sc__list-item')
      .forEach(el => el.classList.add('is-visible'));
  }

  // ── Entry point ────────────────────────────────────────────────────────────

  run() {
    const trad = this.el.querySelector('[data-track="traditional"]');
    const nov  = this.el.querySelector('[data-track="noveliya"]');
    if (trad && nov) this.loop(trad, nov);
  }

  // ── Coordinated loop — both tracks start and reset in sync ─────────────────
  // Promise.all means Noveliya arrives first and visibly waits for traditional,
  // reinforcing the "faster supply chain" message before they reset together.

  async loop(trad, nov) {
    while (true) {
      await Promise.all([this.runTraditional(trad), this.runNoveliya(nov)]);
      await this.wait(1600);
      await Promise.all([this.resetCol(trad), this.resetCol(nov)]);
      await this.wait(500);
    }
  }

  // ── Reset: fade out, snap back to start offset, fade in ───────────────────

  async resetCol(col) {
    const scene   = col.querySelector('[data-scene]');
    const fill    = scene.querySelector('.nv-sc__rail-fill');
    const vehicle = scene.querySelector('[data-vehicle]');
    const pos     = this.nodePositions(scene);

    vehicle.style.transition = 'opacity 350ms ease';
    vehicle.style.opacity = '0';
    fill.style.transition = 'width 450ms ease-in';
    fill.style.width = '0';

    await this.wait(450);

    scene.querySelectorAll('[data-dot], [data-node-connector], [data-node-meta], [data-node-clock]')
      .forEach(el => el.classList.remove('is-visited', 'is-visible', 'is-ticking'));

    // Snap truck to start offset (clears the factory building)
    this.moveTruck(vehicle, this.startX(vehicle, pos), 0);

    await this.wait(120);

    vehicle.style.transition = 'opacity 280ms ease';
    vehicle.style.opacity = '1';
    vehicle.classList.add('is-visible');

    await this.wait(280);
  }

  // ── Traditional animation (4 stops, clock pauses) ────────────────────────

  async runTraditional(col) {
    const scene   = col.querySelector('[data-scene]');
    const fill    = scene.querySelector('.nv-sc__rail-fill');
    const vehicle = scene.querySelector('[data-vehicle]');
    const pos     = this.nodePositions(scene);

    const sx = this.startX(vehicle, pos); // clears factory icon
    const ex = this.endX(vehicle, pos);   // stops before house icon

    const TRAVEL = 900;
    const PAUSE  = 1400;
    const TOTAL  = TRAVEL * 3 + PAUSE * 2; // 5500ms — matches fill duration

    fill.style.transition = `width ${TOTAL}ms linear`;
    this.moveTruck(vehicle, sx, 0);
    this.showNode(scene, 0);
    vehicle.classList.add('is-visible');

    await this.wait(60);
    fill.style.width = '100%';

    // → Node 1 (Groothandel — pip only, no building)
    this.moveTruck(vehicle, pos[1], TRAVEL);
    await this.wait(TRAVEL);
    this.showNode(scene, 1);
    const c1 = scene.querySelector('[data-node-clock="1"]');
    if (c1) await this.showClock(c1, PAUSE);

    // → Node 2 (Winkel — pip only, no building)
    this.moveTruck(vehicle, pos[2], TRAVEL);
    await this.wait(TRAVEL);
    this.showNode(scene, 2);
    const c2 = scene.querySelector('[data-node-clock="2"]');
    if (c2) await this.showClock(c2, PAUSE);

    // → Node 3 (Bij jou thuis) — stop before house building
    this.moveTruck(vehicle, ex, TRAVEL);
    await this.wait(TRAVEL);
    this.showNode(scene, 3);
  }

  // ── Noveliya animation (2 stops, direct) ─────────────────────────────────

  async runNoveliya(col) {
    const scene   = col.querySelector('[data-scene]');
    const fill    = scene.querySelector('.nv-sc__rail-fill');
    const vehicle = scene.querySelector('[data-vehicle]');
    const pos     = this.nodePositions(scene);

    const sx = this.startX(vehicle, pos);
    const ex = this.endX(vehicle, pos);

    const TRAVEL = 2000;

    fill.style.transition = `width ${TRAVEL}ms ease-in-out`;
    this.moveTruck(vehicle, sx, 0);
    this.showNode(scene, 0);
    vehicle.classList.add('is-visible');

    await this.wait(60);
    fill.style.width = '100%';
    this.moveTruck(vehicle, ex, TRAVEL, 'ease-in-out');

    await this.wait(TRAVEL);
    this.showNode(scene, 1);
  }

}

document.querySelectorAll('[data-nv-supply-chain]')
  .forEach(el => new NvSupplyChain(el));
